import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from 'react';
import { modalStore } from 'components/layout/modals/ModalStore';
import { JobPositionVacancies } from './JobPositionVacancies';
import { JobPositionCandidates } from './JobPositionCandidates';
import { JobPositionEdit } from './components/JobPositionEdit';
import { jobPositionStore } from 'ats/pages/job-positions/JobPositionStore';
import { dialogStore } from 'components/layout/modals/DialogStore';
import { notificationStore } from 'components/layout/notifications/NotificationStore';
import { JobPositionHistory } from './JobPositionHistory';
import PinnedPanelToggleButton from 'ats/components/pinned-tasks-panel/PinnedPanelToggleButton';
import { PIN_SCOPES } from 'ats/utils/taskPin';
import JobPositionInsights from './JobPositionInsights';
import { JobPositionChangelog } from './components/JobPositionChangelog';
import moment from 'moment';
import { formatTimeAgo, getValueFromOptions } from 'shared/utils';
import Dropdown from 'ats/components/dropdown/Dropdown';
import HoverMenu from 'ats/components/hover-menu/HoverMenu';
import { observer } from 'mobx-react';
import JobPositionDescription from './JobPositionDescription';
import UserProfileAvatar from 'ats/components/user-profile-avatar/UserProfileAvatar';
import { AddApplicantsModal } from './components/AddApplicantsModal';
import { JobApplicationTaskCreate } from '../pipeline/components/JobApplicationTaskCreate';
import { JobVacancyCreate } from './components/JobVacancyCreate';
import { eventManager } from 'ats/utils/EventManager';
import UrgencyLabel from '../pipeline/components/UrgencyLabel';
import { drawerStore } from 'ats/components/drawer/DrawerStore';
import ClientProfile from '../clients/ClientProfile';
import PipelineJobStats from '../pipeline/components/PipelineJobStats';
import { JobApplicationStore } from '../pipeline/components/JobApplicationStore';

const DAYS_THRESHOLDS = {
	LOW: 45,
	MEDIUM: 60
};

const DAYS_STYLES = {
	LOW: {
		color: '#34D3B3',
		backgroundColor: '#F2FFFC'
	},
	MEDIUM: {
		color: '#F79009',
		backgroundColor: '#FFFCF5'
	},
	HIGH: {
		color: '#E31B54',
		backgroundColor: '#FFF5F6'
	}
};

const TABS = {
	ACTIVITY: 0,
	DESCRIPTION: 1,
	VACANCIES: 2,
	CANDIDATES: 3,
	INSIGHTS: 4,
	CHANGELOG: 5
};

const TAB_SECTION_TITLES = {
	[TABS.ACTIVITY]: 'Activity',
	[TABS.DESCRIPTION]: 'Job Description',
	[TABS.VACANCIES]: 'Vacancies',
	[TABS.CANDIDATES]: 'Candidates',
	[TABS.INSIGHTS]: 'Insights',
	[TABS.CHANGELOG]: 'Log'
};

function JobPositionProfile({
	onClose,
	onSuccess,
	jobPositionId,
	initialTab = TABS.ACTIVITY,
	initialEditingMode = false
}) {
	const [activeTab, setActiveTab] = useState(initialTab);
	const [isDetailsOpen, setIsDetailsOpen] = useState(true);
	const [data, setData] = useState(null);
	const [jobStats, setJobStats] = useState(null);
	const [jobStatsLoading, setJobStatsLoading] = useState(false);
	const [activeKpiFilter, setActiveKpiFilter] = useState(null);
	const jobApplicationStoreRef = useRef(new JobApplicationStore());
	const [insightsIsEditing, setInsightsIsEditing] = useState(false);
	const [isInsightsFormSubmitting, setIsInsightsFormSubmitting] =
		useState(false);
	const [isInsightsFormDirty, setIsInsightsFormDirty] = useState(false);
	const insightsFormRef = useRef(null);

	const loadData = async id => {
		const result = await jobPositionStore.getAuth(id);

		setData(result.data);
	};

	const loadJobStats = useCallback(async id => {
		if (!id) return;

		setJobStatsLoading(true);
		try {
			const result = await jobApplicationStoreRef.current.getJobStats(id);
			const stats = result.data;

			setJobStats(stats);
			setActiveKpiFilter(current =>
				current === 'on_hold' && stats.onHold === 0 ? null : current
			);
		} catch (error) {
			console.error('Error loading job position stats:', error);
			setJobStats(null);
		} finally {
			setJobStatsLoading(false);
		}
	}, []);

	useEffect(() => {
		setActiveKpiFilter(null);
		loadData(jobPositionId);
		loadJobStats(jobPositionId);
	}, [jobPositionId, loadJobStats]);

	useEffect(() => {
		const reloadJobStats = () => loadJobStats(jobPositionId);
		const handleJobApplicationsListReload = ({ jobId }) => {
			if (String(jobId) === String(jobPositionId)) {
				loadJobStats(jobPositionId);
			}
		};

		eventManager.on('taskChanged', reloadJobStats);
		eventManager.on(
			'jobApplicationsListReload',
			handleJobApplicationsListReload
		);

		return () => {
			eventManager.off('taskChanged', reloadJobStats);
			eventManager.off(
				'jobApplicationsListReload',
				handleJobApplicationsListReload
			);
		};
	}, [jobPositionId, loadJobStats]);

	useEffect(() => {
		if (activeTab !== TABS.INSIGHTS) {
			setInsightsIsEditing(false);
		}
	}, [activeTab]);

	useEffect(() => {
		if (!insightsIsEditing) {
			setIsInsightsFormSubmitting(false);
		}
	}, [insightsIsEditing]);

	const handleTabClick = (e, tabId) => {
		e.preventDefault();
		// If already active do nothing.
		if (activeTab === tabId) {
			return;
		}
		setActiveTab(tabId);
	};

	const handleKpiFilterChange = useCallback(filterId => {
		setActiveKpiFilter(filterId);
		setActiveTab(TABS.CANDIDATES);
	}, []);

	const handleSuccess = () => {
		onSuccess && onSuccess();
		loadData(jobPositionId);
		loadJobStats(jobPositionId);
	};

	useEffect(() => {
		const handleJobPositionChanged = ({ jobPositionId }) => {
			if (jobPositionId === data?.id) {
				loadData(jobPositionId);
			}
		};

		eventManager.on('jobPositionChanged', handleJobPositionChanged);

		return () => {
			eventManager.off('jobPositionChanged', handleJobPositionChanged);
		};
	}, [data?.id]);

	const handleJobPositionStatusEdit = (jobPosition, status) => {
		dialogStore.openDialog(
			{
				title: 'Update Job Position Status',
				message: 'The Job Position Status will be changed. Are you sure?',
				confirmLabel: 'Confirm',
				confirmButtonClass: 'is-primary'
			},
			() =>
				jobPositionStore
					.update(jobPosition.id, { job: { status } })
					.then(result => {
						if (result.status === 'success') {
							notificationStore.pushSuccessNotification(
								'Job Position Status updated',
								'The Job Position Status has been successfully updated.'
							);
							handleSuccess();
						} else {
							notificationStore.pushErrorNotification(
								'Update Failed',
								'Failed to update the Job Position Status'
							);
						}
					})
		);
	};

	const handleJobPositionEdit = jobPosition => {
		modalStore.openModal(
			{
				title: 'Edit Job Position',
				size: '2xl',
				position: 'center',
				saveButtonLabel: 'Update'
			},
			<JobPositionEdit job={jobPosition} handleSuccess={handleSuccess} />
		);
	};

	const handleJobPositionDelete = jobPosition => {
		dialogStore.openDialog(
			{
				title: 'Delete Job Position',
				message: 'This Job Position entry will be deleted. Are you sure?',
				confirmLabel: 'Delete',
				confirmButtonClass: 'is-danger'
			},
			() =>
				jobPositionStore.delete(jobPosition.id).then(result => {
					if (result.status === 'success') {
						onClose && onClose();
						notificationStore.pushSuccessNotification(
							'Job Position Deleted',
							'The Job Position has been successfully deleted.'
						);
						onSuccess && onSuccess();
					} else {
						notificationStore.pushErrorNotification(
							'Delete Failed',
							'Failed to delete the Job Position'
						);
					}
				})
		);
	};

	const handleJobPositionDuplicate = jobPosition => {
		dialogStore.openDialog(
			{
				title: 'Duplicate Job Position',
				message: 'This Job Position entry will be duplicated. Are you sure?',
				confirmLabel: 'Duplicate',
				confirmButtonClass: 'is-primary'
			},
			() =>
				jobPositionStore.duplicate(jobPosition.id).then(result => {
					if (result.status === 'success') {
						notificationStore.pushSuccessNotification(
							'Job Position Duplicated',
							'The Job Position has been successfully duplicated.'
						);
						onSuccess();
					} else {
						notificationStore.pushErrorNotification(
							'Duplicate Failed',
							'Failed to duplicate the Job Position'
						);
					}
				})
		);
	};

	const getStatus = value => {
		return jobPositionStore.statusOptions.find(
			status => status.value === value
		);
	};

	const getUrgency = value => {
		return jobPositionStore.urgencyOptions.find(
			urgency => urgency.value === value
		);
	};

	const getPriority = value => {
		return jobPositionStore.priorityOptions.find(
			priority => priority.value === value
		);
	};

	const menuItems = [
		{
			label: 'Change Status',
			subItems: jobPositionStore.statusOptions
				.filter(status => status.value !== data?.status)
				.map(status => {
					return {
						...status,
						label: (
							<span style={{ color: status.colors.text }}>{status.text}</span>
						),
						onClick: async () => {
							await jobPositionStore.update(data.id, {
								job: { status: status.value }
							});
							handleSuccess();
						}
					};
				})
		},
		{
			label: 'Change Priority',
			subItems: jobPositionStore.priorityOptions
				.filter(priority => priority.value !== data?.priority)
				.map(priority => {
					return {
						...priority,
						label: (
							<div
								className={`job-position-priority priority-${priority.value}`}>
								{priority.text}
							</div>
						),
						onClick: async () => {
							await jobPositionStore.update(data.id, {
								job: { priority: priority.value }
							});
							handleSuccess();
						}
					};
				})
		},
		{
			label: 'Change Urgency',
			subItems: jobPositionStore.urgencyOptions
				.filter(urgency => urgency.value !== data?.urgency)
				.map(urgency => {
					return {
						...urgency,
						label: (
							<UrgencyLabel
								mode="text"
								value={urgency.value}
								text={urgency.text}
							/>
						),
						onClick: async () => {
							await jobPositionStore.update(data.id, {
								job: { urgency: urgency.value }
							});
							handleSuccess();
						}
					};
				})
		},
		{
			label: 'Edit',
			onClick: () => {
				handleJobPositionEdit(data);
			}
		},
		{
			label: 'Delete',
			onClick: () => {
				handleJobPositionDelete(data);
			}
		},
		{
			label: 'Duplicate',
			onClick: () => {
				handleJobPositionDuplicate(data);
			}
		}
	];

	const getDaysOpen = (createdAt, closingDate) => {
		if (!createdAt) return '-';
		const start = moment(createdAt);
		const end = closingDate ? moment(closingDate) : moment();
		const days = end.diff(start, 'days');
		return days >= 0 ? days : '-';
	};

	const getDaysOpenStyle = days => {
		if (days === '-' || days < 0) return {};

		if (days <= DAYS_THRESHOLDS.LOW) {
			return DAYS_STYLES.LOW;
		}

		if (days <= DAYS_THRESHOLDS.MEDIUM) {
			return DAYS_STYLES.MEDIUM;
		}

		return DAYS_STYLES.HIGH;
	};

	const openVacancies = useMemo(
		() =>
			data?.vacancies?.filter(vacancy =>
				['open', 'on_hold'].includes(vacancy.status)
			) || [],
		[data?.vacancies]
	);

	const jobStatus = getStatus(data?.status);

	// Calculate the days open for the job position or the longest open vacancy
	const daysOpen = useMemo(() => {
		let result = '-';

		const sorted = openVacancies
			.map(vacancy => getDaysOpen(vacancy.createdAt, vacancy.closingDate))
			.filter(d => typeof d === 'number')
			.sort((a, b) => b - a);

		if (sorted.length > 0) {
			result = sorted[0];
		}

		return result;
	}, [openVacancies]);

	const openAddCandidateModal = () => {
		modalStore.openModal(
			{ title: 'Add Candidate', size: 'xl', position: 'center' },
			<AddApplicantsModal
				job={data}
				handleSuccess={() => {
					handleSuccess();
					eventManager.emit('jobApplicationsListReload', {
						jobId: data.id
					});
				}}
			/>
		);
	};

	const handleClientNameClick = clientId => {
		drawerStore.openDrawer(
			{
				containerId: ['job-positions', 'pipeline', 'applicants', 'clients'],
				drawerId: drawerStore.DRAWER_IDS.CLIENT_PROFILE,
				showHeader: false,
				props: {
					clientId: clientId,
					onSuccess: () => {
						onSuccess && onSuccess();
					},
					onClose: () => {
						drawerStore.closeDrawer();
					}
				}
			},
			ClientProfile
		);
	};

	const actionButtons = {
		[TABS.ACTIVITY]: [
			{
				label: 'Add Candidate',
				onClick: openAddCandidateModal
			},
			{
				label: 'Add Task',
				onClick: () => {
					modalStore.openModal(
						{ title: 'Create Task', size: 'xl', position: 'center' },
						<JobApplicationTaskCreate
							handleSuccess={handleSuccess}
							job={data}
						/>
					);
				}
			}
		],
		[TABS.DESCRIPTION]: [
			{
				label: 'Add Candidate',
				onClick: openAddCandidateModal
			}
		],
		[TABS.VACANCIES]: [
			{
				label: 'Add Candidate',
				onClick: openAddCandidateModal
			},
			{
				label: 'Add Vacancy',
				onClick: () => {
					modalStore.openModal(
						{ title: 'Create Job Vacancy', size: 'xl', position: 'center' },
						<JobVacancyCreate job={data} handleSuccess={handleSuccess} />
					);
				}
			}
		],
		[TABS.CANDIDATES]: [
			{
				label: 'Add Candidate',
				onClick: openAddCandidateModal
			}
		],
		[TABS.INSIGHTS]: [
			...(insightsIsEditing
				? []
				: [
						{
							iconOnly: true,
							iconSrc: '/ats/icons/pencil-blue-icon.svg',
							ariaLabel: 'Edit insights',
							onClick: () => setInsightsIsEditing(true)
						}
					])
		],
		[TABS.CHANGELOG]: []
	};

	const renderTabActionButtons = tabId =>
		(actionButtons[tabId] ?? []).map((button, index) => {
			if (button.iconOnly) {
				return (
					<button
						key={`${tabId}-icon-${index}`}
						type="button"
						className="ats-button-soft ats-button-soft--icon-only"
						aria-label={button.ariaLabel}
						onClick={button.onClick}>
						<img src={button.iconSrc} alt="" width={16} height={16} />
					</button>
				);
			}
			return (
				<button
					key={`${tabId}-${button.label}-${index}`}
					type="button"
					className="ats-button-soft"
					onClick={button.onClick}>
					{button.label}
				</button>
			);
		});

	return (
		<div className="JobPositionProfile">
			<div className="drawer-header">
				<div className="job-profile-header-details">
					{data?.urgency && (
						<UrgencyLabel
							mode="icon"
							value={data.urgency}
							text={getUrgency(data.urgency)?.text}
						/>
					)}
					{data?.priority && (
						<div
							data-tooltip={`Priority: ${getPriority(data.priority)?.text}`}
							className={`job-position-priority priority-${data.priority} tooltip is-tooltip-right`}>
							{getPriority(data.priority)?.text}
						</div>
					)}
					<span
						className="job-position-client-name"
						onClick={e => {
							e.stopPropagation();
							handleClientNameClick(data?.client?.id);
						}}>
						{data?.client?.name || '--'}
					</span>
				</div>
				<button
					className="drawer-close-btn button-bg-soft-small is-icon-only"
					onClick={() => (onClose ? onClose() : drawerStore.closeDrawer())}>
					<img src="/ats/icons/close-blue-icon.svg" alt="" />
				</button>
			</div>
			<div
				className={`job-position-title-container${isDetailsOpen ? ' is-details-open' : ''}`}>
				<h3>
					<span className="job-position-id">#{data?.id}</span>
					{data?.title}
				</h3>
				<div className={`status-tag ${jobStatus?.value}`}>
					<Dropdown
						value={{ ...jobStatus, label: jobStatus?.text }}
						name="status"
						onChange={value => {
							handleJobPositionStatusEdit(data, value.value);
						}}
						hideSelectedOption
						options={jobPositionStore.statusOptions.map(status => ({
							...status,
							label: (
								<span
									className="job-status"
									style={{
										color: status?.colors.text
									}}>
									{status?.text}
								</span>
							),
							className: status.value.replace(/_/g, '-')
						}))}
					/>
				</div>
				<p className="last-activity">
					{data?.updatedAt ? formatTimeAgo(new Date(data?.updatedAt)) : null}
				</p>
				<HoverMenu
					position="left"
					buttonIcon={
						<img src="/ats/icons/three-dots-icon.svg" alt="Menu button" />
					}
					items={menuItems}
				/>
				<button
					type="button"
					className="expand-details-btn button-bg-soft-small is-icon-only"
					aria-expanded={isDetailsOpen}
					aria-label={isDetailsOpen ? 'Hide job details' : 'Show job details'}
					onClick={() => setIsDetailsOpen(!isDetailsOpen)}>
					<img
						src={`/ats/icons/chevron-${isDetailsOpen ? 'up' : 'down'}-icon.svg`}
						alt=""
					/>
				</button>
			</div>
			{isDetailsOpen ? (
				<div className="job-position-profile-header">
					<div className="job-position-header-bio-container">
						<div className="job-position-bio-wrapper job-position-bio-wrapper--hiring-row">
							<div>
								<p>
									<strong>Hiring Manager:</strong> {data?.hiringManager?.name}
								</p>
							</div>
							<div>
								<div className="is-flex gap-1 is-align-items-center">
									<strong>Owner:</strong>
									{data?.owners?.map(({ owner }, index) => (
										<UserProfileAvatar
											key={owner?.id ?? index}
											imageUrl={owner?.imageUrl}
											name={owner?.name}
										/>
									))}
								</div>
								<div className="is-flex gap-1 is-align-items-center">
									<strong>Account Manager:</strong>
									{data?.accountManager && (
										<UserProfileAvatar
											imageUrl={data?.accountManager?.imageUrl}
											name={data?.accountManager?.name}
											tooltipPosition="left"
										/>
									)}
								</div>
								<div className="is-flex gap-1 is-align-items-center">
									<strong>Talent Leader:</strong>
									{data?.talentLeader && (
										<UserProfileAvatar
											imageUrl={data?.talentLeader?.imageUrl}
											name={data?.talentLeader?.name}
											tooltipPosition="left"
										/>
									)}
								</div>
							</div>
						</div>
						<div className="job-position-bio-wrapper grey-bg-wrapper">
							<div className="timezone-wrapper">
								<img src="/ats/icons/user-icon.svg" alt="User icon" />
								{data?.role?.name ? (
									<span>{data?.role?.name}</span>
								) : (
									<button
										type="button"
										className="add-item-btn"
										onClick={() => handleJobPositionEdit(data)}>
										Add Role
									</button>
								)}
							</div>
							<div className="timezone-wrapper">
								<img src="/ats/icons/clock-icon.svg" alt="Clock icon" />
								{data?.timezones && data?.timezones.length > 0 ? (
									<>
										<span>{data?.timezones[0].timezone?.abbr}</span>
										{data?.timezones.slice(1).length > 0 ? (
											<span
												className="tooltip is-tooltip-top"
												data-tooltip={data?.timezones
													.slice(1)
													.map(timezone => timezone.timezone?.abbr)
													.join(' - ')}>
												+{data?.timezones.slice(1).length} More
											</span>
										) : null}
									</>
								) : (
									<button
										type="button"
										className="add-item-btn"
										onClick={() => handleJobPositionEdit(data)}>
										Add Time Zone
									</button>
								)}
							</div>
							<div className="location-wrapper">
								<img src="/ats/icons/location-icon.svg" alt="Location icon" />
								{data?.locations && data?.locations.length > 0 ? (
									<>
										<span>{data?.locations[0].location?.name}</span>
										{data?.locations.slice(1).length > 0 ? (
											<span
												className="tooltip is-tooltip-top"
												data-tooltip={data?.locations
													.slice(1)
													.map(location => location.location?.name)
													.join(' - ')}>
												+{data?.locations.slice(1).length} More
											</span>
										) : null}
									</>
								) : (
									<button
										type="button"
										className="add-item-btn"
										onClick={() => handleJobPositionEdit(data)}>
										Add Location
									</button>
								)}
							</div>
						</div>
						<div className="job-position-bio-wrapper job-position-bio-wrapper--tags-and-stats">
							<div className="is-flex gap-2 is-flex-grow-1 job-position-bio-tags">
								{data?.workSchedule && (
									<span className="bio-tag">
										{getValueFromOptions(
											data?.workSchedule,
											jobPositionStore.workScheduleOptions
										)}
									</span>
								)}
								{data?.term && (
									<span className="bio-tag">
										{getValueFromOptions(
											data?.term,
											jobPositionStore.termOptions
										)}
									</span>
								)}
								{data?.locationType && (
									<span className="bio-tag">
										{getValueFromOptions(
											data?.locationType,
											jobPositionStore.locationTypeOptions
										)}
									</span>
								)}
							</div>
							<div className="job-position-data-numbers">
								<p>
									<strong>Created:</strong>{' '}
									{data?.createdAt
										? moment(data?.createdAt).format('ll')
										: '--'}
								</p>
								<hr className="data-numbers-separator" />
								<p>
									<strong>Open Vacancies</strong>{' '}
									<span className="number-data">{openVacancies.length}</span>
								</p>
								<hr className="data-numbers-separator" />
								<p>
									<strong>Vacancies Aging</strong>{' '}
									<span
										className="number-data"
										style={getDaysOpenStyle(daysOpen)}>
										{daysOpen}
									</span>
								</p>
							</div>
						</div>
					</div>
				</div>
			) : null}
			<div className="job-position-pipeline-stats">
				<PipelineJobStats
					activeFilter={activeKpiFilter}
					loading={jobStatsLoading}
					onFilterChange={handleKpiFilterChange}
					stats={jobStats}
				/>
			</div>
			<div className="job-position-profile-content">
				<div className="tabbed-panel">
					<div className="tabs">
						<ul>
							<li
								className={
									'tabbed-panel-option ' +
									(activeTab === TABS.ACTIVITY ? 'is-active' : '')
								}>
								<a href="void" onClick={e => handleTabClick(e, TABS.ACTIVITY)}>
									<span>Activity</span>
								</a>
							</li>
							<li
								className={
									'tabbed-panel-option ' +
									(activeTab === TABS.DESCRIPTION ? 'is-active' : '')
								}>
								<a
									href="void"
									onClick={e => handleTabClick(e, TABS.DESCRIPTION)}>
									<span>Job Description</span>
								</a>
							</li>
							<li
								className={
									'tabbed-panel-option ' +
									(activeTab === TABS.VACANCIES ? 'is-active' : '')
								}>
								<a href="void" onClick={e => handleTabClick(e, TABS.VACANCIES)}>
									<span>Vacancies</span>
								</a>
							</li>
							<li
								className={
									'tabbed-panel-option ' +
									(activeTab === TABS.CANDIDATES ? 'is-active' : '')
								}>
								<a
									href="void"
									onClick={e => handleTabClick(e, TABS.CANDIDATES)}>
									<span>Candidates</span>
								</a>
							</li>
							<li
								className={
									'tabbed-panel-option ' +
									(activeTab === TABS.INSIGHTS ? 'is-active' : '')
								}>
								<a href="void" onClick={e => handleTabClick(e, TABS.INSIGHTS)}>
									<span>Insights</span>
								</a>
							</li>
							<li
								className={
									'tabbed-panel-option ' +
									(activeTab === TABS.CHANGELOG ? 'is-active' : '')
								}>
								<a href="void" onClick={e => handleTabClick(e, TABS.CHANGELOG)}>
									<span>Log</span>
								</a>
							</li>
						</ul>
					</div>
					<div id="tabs-content-wrapper" className="tabs-content-wrapper">
						<div
							className={`box tabbed-panel-content ${activeTab === TABS.ACTIVITY ? 'is-active' : ''}`}
							id="activity">
							<div className="job-position-tab-section-header">
								<div className="job-position-tab-section-title">
									{TAB_SECTION_TITLES[TABS.ACTIVITY]}
								</div>
								<div className="table-action-buttons">
									<PinnedPanelToggleButton scope={PIN_SCOPES.JOB} />
									{renderTabActionButtons(TABS.ACTIVITY)}
								</div>
							</div>
							<JobPositionHistory
								jobPosition={data}
								onSuccess={handleSuccess}
							/>
						</div>
						<div
							className={`box tabbed-panel-content ${activeTab === TABS.DESCRIPTION ? 'is-active' : ''}`}
							id="description">
							<div className="job-position-tab-section-header">
								<div className="job-position-tab-section-title">
									{TAB_SECTION_TITLES[TABS.DESCRIPTION]}
								</div>
								<div className="table-action-buttons">
									{renderTabActionButtons(TABS.DESCRIPTION)}
								</div>
							</div>
							<JobPositionDescription
								jobPosition={data}
								onSuccess={handleSuccess}
								forceEditingMode={initialEditingMode}
							/>
						</div>
						<div
							className={`box tabbed-panel-content ${activeTab === TABS.VACANCIES ? 'is-active' : ''}`}
							id="vacancies">
							<div className="job-position-tab-section-header">
								<div className="job-position-tab-section-title">
									{TAB_SECTION_TITLES[TABS.VACANCIES]}
								</div>
								<div className="table-action-buttons">
									{renderTabActionButtons(TABS.VACANCIES)}
								</div>
							</div>
							<JobPositionVacancies
								jobPosition={data}
								onSuccess={handleSuccess}
							/>
						</div>
						<div
							className={`box tabbed-panel-content ${activeTab === TABS.CANDIDATES ? 'is-active' : ''}`}
							id="candidates">
							<div className="job-position-tab-section-header">
								<div className="job-position-tab-section-title">
									{TAB_SECTION_TITLES[TABS.CANDIDATES]}
								</div>
								<div className="table-action-buttons">
									{renderTabActionButtons(TABS.CANDIDATES)}
								</div>
							</div>
							<JobPositionCandidates
								jobPosition={data}
								kpiFilter={activeKpiFilter}
								onSuccess={handleSuccess}
							/>
						</div>
						<div
							className={`box tabbed-panel-content ${activeTab === TABS.INSIGHTS ? 'is-active' : ''}`}
							id="insights">
							<div className="job-position-tab-section-header job-position-tab-section-header--insights">
								<div className="job-position-tab-section-title">
									{TAB_SECTION_TITLES[TABS.INSIGHTS]}
								</div>
								<div className="job-position-tab-section-trailing">
									{!insightsIsEditing && (
										<div className="job-position-insights-header-meta is-flex gap-1 is-align-items-center">
											<UserProfileAvatar
												imageUrl={data?.updatedBy?.imageUrl}
												name={data?.updatedBy?.name}
											/>
											<span>
												Updated:{' '}
												{data?.updatedAt
													? formatTimeAgo(new Date(data.updatedAt))
													: null}
											</span>
										</div>
									)}
									{insightsIsEditing ? (
										<div className="job-position-insights-header-actions">
											<button
												type="button"
												className="cancel-btn"
												onClick={() => insightsFormRef.current?.cancelEdit()}>
												Cancel
											</button>
											<button
												type="button"
												className="save-btn"
												disabled={
													!isInsightsFormDirty || isInsightsFormSubmitting
												}
												onClick={() => insightsFormRef.current?.submit()}>
												Save
											</button>
										</div>
									) : (
										<div className="table-action-buttons">
											{renderTabActionButtons(TABS.INSIGHTS)}
										</div>
									)}
								</div>
							</div>
							<JobPositionInsights
								ref={insightsFormRef}
								jobPosition={data}
								onSuccess={handleSuccess}
								isEditing={insightsIsEditing}
								onIsEditingChange={next => {
									setInsightsIsEditing(next);
									if (!next) {
										setIsInsightsFormDirty(false);
									}
								}}
								onSubmittingChange={setIsInsightsFormSubmitting}
								onDirtyChange={setIsInsightsFormDirty}
							/>
						</div>
						<div
							className={`box tabbed-panel-content ${activeTab === TABS.CHANGELOG ? 'is-active' : ''}`}
							id="changelog">
							<JobPositionChangelog
								jobPosition={data}
								onSuccess={handleSuccess}
							/>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}

export default observer(JobPositionProfile);
